/**
 * Reusable table component with sorting
 */

import './Table.css';

/**
 * Table component with sortable columns
 * @param {Object} props - Component props
 * @param {Array} props.columns - Column definitions
 * @param {Array} props.data - Table data rows
 * @param {string} props.sortBy - Current sort column
 * @param {string} props.sortOrder - Current sort order (asc/desc)
 * @param {Function} props.onSort - Sort handler
 * @param {Function} props.onRowClick - Row click handler
 * @param {boolean} props.loading - Loading state
 * @param {string} props.emptyMessage - Message when no data
 * @returns {React.ReactElement} Table element
 */
export default function Table({
  columns,
  data,
  sortBy,
  sortOrder,
  onSort,
  onRowClick,
  loading = false,
  emptyMessage = 'No data available',
  className = ''
}) {
  const handleHeaderClick = (column) => {
    if (column.sortable && onSort) {
      const newOrder = sortBy === column.key && sortOrder === 'asc' ? 'desc' : 'asc';
      onSort(column.key, newOrder);
    }
  };

  const getSortIcon = (column) => {
    if (!column.sortable) return null;
    if (sortBy !== column.key) return <span className="table__sort-icon">↕</span>;
    return (
      <span className="table__sort-icon table__sort-icon--active">
        {sortOrder === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  return (
    <div className={`table-container ${className}`}>
      <table className="table">
        <thead className="table__head">
          <tr>
            {columns.map(column => (
              <th
                key={column.key}
                className={`table__header ${column.sortable ? 'table__header--sortable' : ''}`}
                style={{ width: column.width }}
                onClick={() => handleHeaderClick(column)}
              >
                <span className="table__header-content">
                  {column.label}
                  {getSortIcon(column)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="table__body">
          {loading ? (
            <tr>
              <td colSpan={columns.length} className="table__cell table__cell--loading">
                <div className="loading-spinner" />
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="table__cell table__cell--empty">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, index) => (
              <tr
                key={row.id || index}
                className={`table__row ${onRowClick ? 'table__row--clickable' : ''}`}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map(column => (
                  <td key={column.key} className="table__cell">
                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
